/*
 * Countries a phone number can be dialled from, for the desk's country
 * picker and for showing a stored number. Numbers are stored E.164 ("+919811044821");
 * this only splits them back into country and national part.
 *
 * India first, then the neighbours and the places guests most often fly in
 * from; the picker searches the rest. Where two share a code (+1, +7) the
 * first listed is the one a bare code resolves to.
 */

export type Country = { iso: string; name: string; dial: string };

export const COUNTRIES: readonly Country[] = [
  { iso: "IN", name: "India", dial: "91" },
  { iso: "NP", name: "Nepal", dial: "977" },
  { iso: "BT", name: "Bhutan", dial: "975" },
  { iso: "BD", name: "Bangladesh", dial: "880" },
  { iso: "LK", name: "Sri Lanka", dial: "94" },
  { iso: "MV", name: "Maldives", dial: "960" },
  { iso: "IL", name: "Israel", dial: "972" },
  { iso: "AE", name: "United Arab Emirates", dial: "971" },
  { iso: "GB", name: "United Kingdom", dial: "44" },
  { iso: "US", name: "United States", dial: "1" },
  { iso: "CA", name: "Canada", dial: "1" },
  { iso: "AU", name: "Australia", dial: "61" },
  { iso: "NZ", name: "New Zealand", dial: "64" },
  { iso: "DE", name: "Germany", dial: "49" },
  { iso: "FR", name: "France", dial: "33" },
  { iso: "NL", name: "Netherlands", dial: "31" },
  { iso: "IT", name: "Italy", dial: "39" },
  { iso: "ES", name: "Spain", dial: "34" },
  { iso: "PT", name: "Portugal", dial: "351" },
  { iso: "CH", name: "Switzerland", dial: "41" },
  { iso: "AT", name: "Austria", dial: "43" },
  { iso: "BE", name: "Belgium", dial: "32" },
  { iso: "IE", name: "Ireland", dial: "353" },
  { iso: "SE", name: "Sweden", dial: "46" },
  { iso: "NO", name: "Norway", dial: "47" },
  { iso: "DK", name: "Denmark", dial: "45" },
  { iso: "FI", name: "Finland", dial: "358" },
  { iso: "PL", name: "Poland", dial: "48" },
  { iso: "CZ", name: "Czechia", dial: "420" },
  { iso: "GR", name: "Greece", dial: "30" },
  { iso: "RU", name: "Russia", dial: "7" },
  { iso: "UA", name: "Ukraine", dial: "380" },
  { iso: "TR", name: "Türkiye", dial: "90" },
  { iso: "SA", name: "Saudi Arabia", dial: "966" },
  { iso: "QA", name: "Qatar", dial: "974" },
  { iso: "KW", name: "Kuwait", dial: "965" },
  { iso: "OM", name: "Oman", dial: "968" },
  { iso: "BH", name: "Bahrain", dial: "973" },
  { iso: "SG", name: "Singapore", dial: "65" },
  { iso: "MY", name: "Malaysia", dial: "60" },
  { iso: "TH", name: "Thailand", dial: "66" },
  { iso: "ID", name: "Indonesia", dial: "62" },
  { iso: "PH", name: "Philippines", dial: "63" },
  { iso: "VN", name: "Vietnam", dial: "84" },
  { iso: "CN", name: "China", dial: "86" },
  { iso: "HK", name: "Hong Kong", dial: "852" },
  { iso: "JP", name: "Japan", dial: "81" },
  { iso: "KR", name: "South Korea", dial: "82" },
  { iso: "PK", name: "Pakistan", dial: "92" },
  { iso: "ZA", name: "South Africa", dial: "27" },
  { iso: "KE", name: "Kenya", dial: "254" },
  { iso: "NG", name: "Nigeria", dial: "234" },
  { iso: "EG", name: "Egypt", dial: "20" },
  { iso: "BR", name: "Brazil", dial: "55" },
  { iso: "AR", name: "Argentina", dial: "54" },
  { iso: "MX", name: "Mexico", dial: "52" },
];

export const DEFAULT_COUNTRY = COUNTRIES[0];

export const countryByIso = (iso: string | null | undefined): Country => COUNTRIES.find((c) => c.iso === iso) ?? DEFAULT_COUNTRY;

/** "IN" → 🇮🇳, from the two regional-indicator letters. */
export const flagOf = (iso: string): string => String.fromCodePoint(...[...iso.toUpperCase()].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));

/**
 * "+44 7911 123456" → United Kingdom and "7911 123456". The longest code that
 * matches wins (+971 before +97…); `prefer` breaks a tie between countries
 * sharing a code, so a number picked as Canada stays Canada. Undefined when
 * the text doesn't start with "+" or no country matches.
 */
export function splitPhone(text: string, prefer?: string): { country: Country; national: string } | undefined {
  const t = text.trim();
  if (!t.startsWith("+")) return undefined;
  const digits = t.slice(1).replace(/\D/g, "");
  const hits = COUNTRIES.filter((c) => digits.startsWith(c.dial));
  if (!hits.length) return undefined;
  const longest = Math.max(...hits.map((c) => c.dial.length));
  const best = hits.filter((c) => c.dial.length === longest);
  const country = best.find((c) => c.iso === prefer) ?? best[0];
  // Keep the spacing the person typed after the code; drop the code itself.
  let seen = 0;
  let cut = 1;
  while (cut < t.length && seen < country.dial.length) {
    if (/\d/.test(t[cut])) seen += 1;
    cut += 1;
  }
  return { country, national: t.slice(cut).trim() };
}
